import React, { useContext, useState, useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Avatar,
  Chip,
  Stack,
  TextField,
  InputAdornment,
  MenuItem,
  useMediaQuery,
  useTheme,
  IconButton
} from '@mui/material';
import { FaSearch, FaEnvelope, FaPhone, FaBuilding, FaUserShield, FaTimes } from 'react-icons/fa';
import { AuthContext } from '../auth/AuthContext';
import { useNavigate } from 'react-router-dom';

const statusColors = {
  active: 'success',
  inactive: 'error',
};

const roleColors = {
  admin: 'success',
  ngo: 'secondary',
  specialist: 'info',
  user: 'primary',
};

const AllAdmins = () => {
  const { allAdmins = [] } = useContext(AuthContext);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate()

  // State for search and filters
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [roleFilter, setRoleFilter] = useState('all');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(isMobile ? 5 : 10);

  // Filtered and searched admins
  const filteredAdmins = useMemo(() => {
    let filtered = Array.isArray(allAdmins) && allAdmins;
    if (statusFilter !== 'all') {
      filtered = filtered.filter(a => a.status === statusFilter);
    }
    if (roleFilter !== 'all') {
      filtered = filtered.filter(a => a.role === roleFilter);
    }
    if (search.trim()) {
      const s = search.trim().toLowerCase();
      filtered = filtered.filter(
        a =>
          a.name?.toLowerCase().includes(s) ||
          a.email?.toLowerCase().includes(s) ||
          a.phone?.toLowerCase().includes(s) ||
          a.organisation?.toLowerCase().includes(s)
      );
    }
    return filtered;
  }, [allAdmins, search, statusFilter, roleFilter]);

  // Pagination
  const pagedAdmins = useMemo(
    () =>
      filteredAdmins.slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage
      ),
    [filteredAdmins, page, rowsPerPage]
  );

  // Unique roles for filter dropdown
  const uniqueRoles = useMemo(
    () => Array.from(new Set(allAdmins.map(a => a.role))).filter(Boolean),
    [allAdmins]
  );
  // console.log(pagedAdmins)

  return (
    <Box sx={{ p: { xs: 1, md: 4 }, maxWidth: 1200, mx: 'auto' }}>
      
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        justifyContent="space-between"
        spacing={2}
        sx={{ mb: 2 }}
      >
        <Stack direction="row" alignItems="center" spacing={2}>
          <IconButton
            onClick={() => navigate(-1)}
            sx={{
              color: 'red',
              border: '1.5px solid #2e7d32',
              background: '#fff',
              mr: 1,
              '&:hover': { background: '#e8f5e9' }
            }}
            size="large"
          >
            <FaTimes />
          </IconButton>
          <Typography
            variant="h4"
            fontWeight="bold"
            sx={{
              color: '#fff',
              fontFamily: 'Playfair Display',
              textAlign: { xs: 'center', md: 'center' },
            }}
          >
            Admins & Specialists Management
          </Typography>
        </Stack>

  <Box>
    <button
      onClick={() => navigate('/register-specialist')}
      style={{
        padding: '8px 16px',
        fontWeight: 'bold',
        borderRadius: '8px',
        backgroundColor: '#2e7d32',
        color: 'white',
        border: 'none',
        cursor: 'pointer',
        fontSize: '14px'
      }}
    >
      + Register Specialist
    </button>
  </Box>
</Stack>

      <Paper
        elevation={3}
        sx={{
          p: 2,
          borderRadius: 4,
          background: 'linear-gradient(135deg, #e0f7fa 0%, #f0fdf4 100%)',
          mb: 3,
        }}
      >
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={2}
          sx={{ mb: 2, alignItems: { sm: 'center' } }}
        >
          <TextField
            variant="outlined"
            size="small"
            placeholder="Search by name, email, phone, organisation..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <FaSearch color="#1976d2" />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: 220, background: '#fff', borderRadius: 2, flex: 1 }}
          />
          <TextField
            select
            label="Status"
            value={statusFilter}
            onChange={e => setStatusFilter(e.target.value)}
            size="small"
            sx={{ minWidth: 120, background: '#fff', borderRadius: 2 }}
          >
            <MenuItem value="all">All Status</MenuItem>
            <MenuItem value="active">Active</MenuItem>
            <MenuItem value="inactive">Inactive</MenuItem>
          </TextField>
          <TextField
            select
            label="Role"
            value={roleFilter}
            onChange={e => setRoleFilter(e.target.value)}
            size="small"
            sx={{ minWidth: 120, background: '#fff', borderRadius: 2 }}
          >
            <MenuItem value="all">All Roles</MenuItem>
            {uniqueRoles.map(role => (
              <MenuItem key={role} value={role}>
                {role.charAt(0).toUpperCase() + role.slice(1)}
              </MenuItem>
            ))}
          </TextField>
        </Stack>
        <TableContainer sx={{ borderRadius: 3, boxShadow: 2 }}>
          <Table size={isMobile ? 'small' : 'medium'}>
            <TableHead>
              <TableRow sx={{ background: 'linear-gradient(90deg, #43e97b 0%, #38f9d7 100%)' }}>
                <TableCell>#</TableCell>
                <TableCell>Profile</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Phone</TableCell>
                <TableCell>Organisation</TableCell>
                <TableCell>Role</TableCell>
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {pagedAdmins.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ color: '#888', py: 4 }}>
                    No admins found.
                  </TableCell>
                </TableRow>
              ) : (
                Array.isArray(pagedAdmins) && pagedAdmins.map((admin, idx) => (
                  <TableRow
                    key={admin.id}
                    sx={{
                      background: idx % 2 ? '#f9f9f9' : '#f0fdf4',
                      transition: 'background 0.3s',
                    }}
                  >
                    <TableCell>{page * rowsPerPage + idx + 1}</TableCell>
                    <TableCell>
                      <Avatar
                        src={admin?.profile}
                        alt={admin.name}
                        sx={{
                          width: 40,
                          height: 40,
                          border: '2px solid #1976d2',
                          bgcolor: '#e3f2fd',
                        }}
                      >
                        <FaUserShield color="#1976d2" />
                      </Avatar>
                    </TableCell>
                    <TableCell>
                      <Typography fontWeight="bold">{admin.name}</Typography>
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <FaEnvelope color="#1976d2" />
                        <Typography>{admin.email}</Typography>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <FaPhone color="#388e3c" />
                        <Typography>{admin.phone || 'N/A'}</Typography>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <FaBuilding color="#388e3c" />
                        <Typography>{admin.organisation || 'N/A'}</Typography>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={admin.role?.charAt(0).toUpperCase() + admin.role?.slice(1)}
                        color={roleColors[admin.role] || 'default'}
                        size="small"
                        sx={{
                          fontWeight: 'bold',
                          fontSize: 13,
                          bgcolor: '#e8f5e9',
                          color: '#388e3c',
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={admin.status?.charAt(0).toUpperCase() + admin.status?.slice(1)}
                        color={statusColors[admin.status] || 'default'}
                        size="small"
                        sx={{
                          fontWeight: 'bold',
                          fontSize: 13,
                          bgcolor: admin.status === 'active' ? '#e8f5e9' : '#ffebee',
                          color: admin.status === 'active' ? '#388e3c' : '#d32f2f',
                        }}
                      />
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={filteredAdmins.length}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={e => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={isMobile ? [5, 10] : [5, 10, 20]}
          sx={{
            mt: 2,
            '.MuiTablePagination-toolbar': { px: { xs: 0, sm: 2 } },
            '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': { fontSize: 14 },
          }}
        />
      </Paper>
    </Box>
  );
};

export default AllAdmins;